import {Component, ElementRef, Injectable, Input, NgZone, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormControl} from '@angular/forms';
import {NgbDateParserFormatter, NgbDatepickerI18n, NgbDateStruct} from '@ng-bootstrap/ng-bootstrap';
import {TranslateService} from '@ngx-translate/core';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/fromEvent';

const I18N_VALUES = {
  en: {
    weekdays: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
    months: [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ]
  },
  pl: {
    weekdays: ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd'],
    months: [
      'Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze',
      'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'
    ]
  }
};

@Injectable()
export class I18nDatePicker extends NgbDatepickerI18n {
  private lang = 'en';

  constructor(private translate: TranslateService) {
    super();
    const storedLang = localStorage.getItem('lang');
    this.lang = storedLang && I18N_VALUES[storedLang] ? storedLang : 'en';

    this.translate.onLangChange.subscribe(event => {
      this.lang = I18N_VALUES[event.lang] ? event.lang : 'en';
    });
  }

  getWeekdayShortName(weekday: number): string {
    return I18N_VALUES[this.lang].weekdays[weekday - 1];
  }

  getMonthShortName(month: number): string {
    return I18N_VALUES[this.lang].months[month - 1];
  }

  getMonthFullName(month: number): string {
    return I18N_VALUES[this.lang].months[month - 1];
  }

  getDayAriaLabel(date: NgbDateStruct): string {
    return `${date.day}-${date.month}-${date.year}`;
  }
}

@Injectable()
export class CustomDateFormatter extends NgbDateParserFormatter {

  parse(value: string): NgbDateStruct | null {
    if (!value) return null;
    // Trim przed podziałem: bez niego spacje wokół wartości dałyby dodatkowe człony.
    const parts = value.trim().split(/[-.:\/\s]+/);
    if (parts.length !== 3) return null;
    return {
      day: parseInt(parts[0], 10),
      month: parseInt(parts[1], 10),
      year: parseInt(parts[2], 10)
    };
  }

  format(date: NgbDateStruct | null): string {
    if (!date) return '';
    const day = date.day.toString().padStart(2, '0');
    const month = date.month.toString().padStart(2, '0');
    return `${day}-${month}-${date.year}`;
  }
}

@Component({
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  providers: [
    {provide: NgbDatepickerI18n, useClass: I18nDatePicker},
    {provide: NgbDateParserFormatter, useClass: CustomDateFormatter}
  ]
})
export class DatePickerComponent implements OnInit, OnDestroy {

  @ViewChild('d', {static: false}) d: { close: () => void; };
  @Input() parentFormControl: FormControl;
  @Input() maxWidth: number;
  @Input() placeholder: string = null;
  @Input() limitDate: boolean = true;
  @Input() futureDateOnly: boolean = null;
  @Input() minDateIn = null;
  @Input() maxDateIn = null;

  private subscription: any;
  private date = new Date();

  constructor(
    private eRef: ElementRef,
    private translate: TranslateService,
    private zone: NgZone
  ) {
    setTimeout(() => {
      if (this.futureDateOnly) {
        this.minDateFuture();
        this.maxDateFuture();
      } else {
        this.minDate();
        this.maxDate();
      }
    }, 500);
  }

  ngOnInit() {
    this.zone.runOutsideAngular(() => {
      this.subscription = Observable.fromEvent(document, 'click').subscribe((event: Event) => {
        setTimeout(() => {
          const target = event.target as HTMLElement;
          const datepickerElement = document.querySelector('.ngb-dp');

          const isInsideComponent = this.eRef.nativeElement.contains(target);
          const isInsideDatepicker = datepickerElement && datepickerElement.contains(target);
          const isInDropdowns = target.closest('.ngb-dp-navigation-select') !== null;

          if (isInsideComponent || isInsideDatepicker || isInDropdowns) return;

          this.zone.run(() => {
            if (this.d && this.d.close) this.d.close();
          });
        }, 50);
      });
    });

    const styles = document.createElement('style');
    styles.innerHTML = `
      .bg-primary {
        background-color: #9F9FF8 !important;
        color: #ffffff !important;
        border-radius: 20px;
      }
      .ngb-dp-weekday {
        color: #9b9b9b !important;
        font-style: normal !important;
        font-size: 12px !important;
        font-weight: 400 !important;
        text-align: center !important;
        text-decoration-skip-ink: none !important;
      }
      .btn-link {
        color: #000000 !important;
      }
    `;
    document.head.appendChild(styles);
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  minDate() {
    if (this.minDateIn === null) {
      this.minDateIn = {
        year: this.date.getFullYear() - 120,
        month: this.date.getMonth() + 1,
        day: this.date.getDate()
      };
    }
  }

  maxDate() {
    if (this.maxDateIn === null) {
      this.maxDateIn = this.limitDate
        ? {
          year: this.date.getFullYear(),
          month: this.date.getMonth() + 1,
          day: this.date.getDate()
        }
        : {
          year: this.date.getFullYear() + 100,
          month: this.date.getMonth() + 1,
          day: this.date.getDate()
        };
    }
  }

  minDateFuture() {
    if (this.minDateIn === null) {
      this.minDateIn = {
        year: this.date.getFullYear(),
        month: this.date.getMonth() + 1,
        day: this.date.getDate()
      };
    }
  }

  maxDateFuture() {
    if (this.maxDateIn === null && this.limitDate) {
      this.maxDateIn = {
        year: this.date.getFullYear() + 100,
        month: this.date.getMonth() + 1,
        day: this.date.getDate()
      };
    }
  }
}
